import { Button, Dialog, Input } from '@platform/design-system';
import { useState } from 'react';
import { usePageContext } from '../../usePageContext.ts';
import { ChoiceField } from './ChoiceField.tsx';
import { definitionOf } from './registry.ts';
import { WIDGET_SIZE_CHOICES } from './widget-options.ts';
import type { DashboardWidget, WidgetContext } from './widget-types.ts';
import styles from './Widgets.module.css';

const MAX_TITLE = 60;

export type WidgetSettingsDialogProps = {
  widget: DashboardWidget;
  context: WidgetContext;
  onClose: () => void;
  onSave: (widget: DashboardWidget) => void;
};

export const WidgetSettingsDialog = ({ widget, context, onClose, onSave }: WidgetSettingsDialogProps) => {
  const { t } = usePageContext();
  const [draft, setDraft] = useState<DashboardWidget>(widget);
  const { Render, Fields, titleOf } = definitionOf(draft);
  return (
    <Dialog
      open
      size="wide"
      title={t('dashboard.widgets.settingsTitle')}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              onSave(draft);
            }}
          >
            {t('dashboard.widgets.apply')}
          </Button>
        </>
      }
    >
      <div className={styles['settings']}>
        <div className={styles['fields']}>
          <Input
            label={t('dashboard.widgets.field.title')}
            helperText={t('dashboard.widgets.field.titleHelp')}
            placeholder={titleOf(draft, t)}
            maxLength={MAX_TITLE}
            value={draft.title ?? ''}
            onChange={(event) => {
              const title = event.target.value.trim() === '' ? null : event.target.value;
              setDraft({ ...draft, title });
            }}
          />
          <ChoiceField
            label={t('dashboard.widgets.field.size')}
            value={draft.size}
            choices={WIDGET_SIZE_CHOICES}
            labelOf={(size) => t(`dashboard.widgets.size.${size}`)}
            onChange={(size) => {
              setDraft({ ...draft, size });
            }}
          />
          <Fields
            settings={draft.settings}
            onChange={(settings) => {
              setDraft({ ...draft, settings } as DashboardWidget);
            }}
          />
        </div>
        <section className={styles['preview']} aria-label={t('dashboard.widgets.preview')}>
          <Render widget={draft} context={context} />
        </section>
      </div>
    </Dialog>
  );
};
